import { useEffect, useMemo, useState } from 'react'
import TeamHelmet from '../components/TeamHelmet'
import api from '../services/api'
import {
  normalizePlayableTeamAbbreviation,
  PLAYABLE_TEAM_ABBREVIATIONS,
  type PlayableTeamAbbreviation,
} from '../constants/teamAssets'
import './TeamPerformance.css'

interface GameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
}

interface TeamPerformanceStat {
  team: PlayableTeamAbbreviation
  wins: number
  losses: number
  ties: number
  appearances: number
  winPercentage: number
}

interface TeamColor {
  background: string
  foreground: string
}

const TEAM_COLORS: Record<PlayableTeamAbbreviation, TeamColor> = {
  ATL: { background: '#111111', foreground: '#f64747' },
  BUF: { background: '#0033ff', foreground: '#ff3b3b' },
  CHI: { background: '#f58f12', foreground: '#111111' },
  CIN: { background: '#f56512', foreground: '#111111' },
  CLE: { background: '#f08e16', foreground: '#111111' },
  DAL: { background: '#9fc7e8', foreground: '#17324a' },
  DEN: { background: '#fb8c00', foreground: '#111111' },
  DET: { background: '#74acd7', foreground: '#17324a' },
  GB: { background: '#23ad38', foreground: '#fff7eb' },
  HOU: { background: '#a7cde8', foreground: '#17324a' },
  IND: { background: '#c4d7f0', foreground: '#ffffff' },
  KC: { background: '#ff0f0f', foreground: '#ffe45f' },
  LA: { background: '#3d7fd8', foreground: '#ffe75a' },
  MIA: { background: '#02e0e8', foreground: '#ff8b12' },
  MIN: { background: '#5b4fb5', foreground: '#ffe45f' },
  NE: { background: '#12588f', foreground: '#ff2c2c' },
  NO: { background: '#b38b00', foreground: '#111111' },
  NYG: { background: '#2556b8', foreground: '#fff7eb' },
  NYJ: { background: '#4d7f28', foreground: '#fff7eb' },
  PHX: { background: '#d9d9d9', foreground: '#ff1328' },
  PHI: { background: '#2e6c17', foreground: '#fff7eb' },
  PIT: { background: '#050505', foreground: '#ffe45f' },
  RAI: { background: '#b7b7b7', foreground: '#111111' },
  SD: { background: '#08385e', foreground: '#ffe45f' },
  SEA: { background: '#6aaa55', foreground: '#14335b' },
  SF: { background: '#b38b00', foreground: '#ff342d' },
  TB: { background: '#ffdd42', foreground: '#ffffff' },
  WAS: { background: '#e30d0d', foreground: '#ffe45f' },
}

/**
 * Summarizes performance by Tecmo team across uploaded game results.
 */
export default function TeamPerformance() {
  const [games, setGames] = useState<GameSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoading(true)
        const response = await api.get<GameSummary[]>('/games')
        setGames(response.data ?? [])
        setError(null)
      } catch (err: any) {
        console.error('Failed to load team performance:', err)
        setError(err.response?.data?.error || 'Failed to load team performance.')
      } finally {
        setLoading(false)
      }
    }

    loadGames()
  }, [])

  const teamStats = useMemo(() => computeTeamPerformance(games), [games])
  const wins = useMemo(() => sortTeamStats(teamStats, 'wins'), [teamStats])
  const losses = useMemo(() => sortTeamStats(teamStats, 'losses'), [teamStats])
  const winPercentages = useMemo(() => sortTeamStats(teamStats, 'winPercentage'), [teamStats])
  const appearances = useMemo(() => sortTeamStats(teamStats, 'appearances'), [teamStats])

  if (loading) {
    return <div className="team-performance loading">Loading team performance...</div>
  }

  if (error) {
    return (
      <div className="team-performance">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="team-performance">
      <header className="team-performance-hero">
        <p className="team-performance-kicker">Team Ledger</p>
        <h1>Team Performance</h1>
        <p className="team-performance-subtitle">
          Wins, losses, win rate, and total appearances by playable Tecmo team.
        </p>
      </header>

      <div className="team-performance-grid">
        <PerformanceTable
          title="Wins"
          stats={wins}
          columns={[{ label: 'Wins', render: stat => stat.wins.toLocaleString() }]}
        />
        <PerformanceTable
          title="Losses"
          stats={losses}
          columns={[{ label: 'Losses', render: stat => stat.losses.toLocaleString() }]}
        />
        <PerformanceTable
          title="Win %"
          stats={winPercentages}
          columns={[
            { label: 'Win %', render: stat => `${Math.round(stat.winPercentage * 100)}%` },
            { label: 'Record', render: formatRecord },
          ]}
        />
        <PerformanceTable
          title="Total Appearances"
          stats={appearances}
          columns={[{ label: 'Total Appearances', render: stat => stat.appearances.toLocaleString() }]}
        />
      </div>
    </div>
  )
}

function computeTeamPerformance(games: GameSummary[]): TeamPerformanceStat[] {
  const stats = new Map<PlayableTeamAbbreviation, TeamPerformanceStat>(
    PLAYABLE_TEAM_ABBREVIATIONS.map(team => [
      team,
      {
        team,
        wins: 0,
        losses: 0,
        ties: 0,
        appearances: 0,
        winPercentage: 0,
      },
    ])
  )

  for (const game of games) {
    const homeTeam = normalizePlayableTeamAbbreviation(game.homeTeam)
    const awayTeam = normalizePlayableTeamAbbreviation(game.awayTeam)

    if (!homeTeam || !awayTeam || homeTeam === awayTeam) {
      continue
    }

    const homeStats = stats.get(homeTeam)
    const awayStats = stats.get(awayTeam)
    if (!homeStats || !awayStats) {
      continue
    }

    homeStats.appearances += 1
    awayStats.appearances += 1

    if (game.homeScore > game.awayScore) {
      homeStats.wins += 1
      awayStats.losses += 1
    } else if (game.awayScore > game.homeScore) {
      awayStats.wins += 1
      homeStats.losses += 1
    } else {
      homeStats.ties += 1
      awayStats.ties += 1
    }
  }

  return [...stats.values()].map(stat => {
    const decisions = stat.wins + stat.losses + stat.ties
    return {
      ...stat,
      winPercentage: decisions > 0 ? (stat.wins + stat.ties * 0.5) / decisions : 0,
    }
  })
}

function sortTeamStats(
  stats: TeamPerformanceStat[],
  key: 'wins' | 'losses' | 'winPercentage' | 'appearances'
) {
  return [...stats].sort((left, right) => {
    const valueDiff = right[key] - left[key]
    if (valueDiff !== 0) {
      return valueDiff
    }

    if (key === 'winPercentage') {
      const appearanceDiff = right.appearances - left.appearances
      if (appearanceDiff !== 0) {
        return appearanceDiff
      }
    }

    return left.team.localeCompare(right.team)
  })
}

function formatRecord(stat: TeamPerformanceStat) {
  return `${stat.wins}-${stat.losses}${stat.ties > 0 ? `-${stat.ties}` : ''}`
}

function PerformanceTable({
  title,
  stats,
  columns,
}: {
  title: string
  stats: TeamPerformanceStat[]
  columns: Array<{ label: string; render: (stat: TeamPerformanceStat) => string }>
}) {
  return (
    <section className="team-performance-card">
      <h2>{title}</h2>
      <div className="team-performance-table-wrap">
        <table className={`team-performance-table team-performance-table-${columns.length + 1}-cols`}>
          <colgroup>
            <col className="team-performance-team-col" />
            {columns.map(column => (
              <col
                key={`${title}-${column.label}-col`}
                className="team-performance-value-col"
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Team</th>
              {columns.map(column => (
                <th key={`${title}-${column.label}`}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.map(stat => (
              <tr key={`${title}-${stat.team}`}>
                <td>
                  <TeamBadge team={stat.team} />
                </td>
                {columns.map(column => (
                  <td key={`${title}-${stat.team}-${column.label}`}>{column.render(stat)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function TeamBadge({ team }: { team: PlayableTeamAbbreviation }) {
  const colors = TEAM_COLORS[team]

  return (
    <span
      className="team-performance-badge"
      style={{
        backgroundColor: colors.background,
        color: colors.foreground,
      }}
    >
      <TeamHelmet abbreviation={team} size="sm" className="team-performance-helmet" />
      <span>{team}</span>
    </span>
  )
}
